import { useCallback, useEffect, useRef, useState } from "react";
import { useDataProvider, useGetIdentity } from "ra-core";

import type { CrmDataProvider } from "../providers/types";

/**
 * Fremdrift i veiledningen og oppstartskortet: hvilke id-er som er kvittert
 * ut (PRD US23.4).
 *
 * Fremdriften følger brukeren: basen (tutorial_progress) er fasiten, og
 * localStorage er en hurtigbuffer per bruker så siden tegnes med en gang.
 * Har brukeren ingen rad i basen ennå, lastes det lokale opp, så en ny
 * nettleser aldri nullstiller fremdriften. Bare «Start på nytt» tømmer den.
 * Nøkkelen er versjonert så en fremtidig omlegging av id-ene kan starte på
 * blanke ark.
 */
const LEGACY_KEY = "MiminVeiledning.v1";
const keyFor = (userId: string | number) => `${LEGACY_KEY}.${userId}`;

const parse = (raw: string | null): Set<string> | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return null;
  }
};

// Før fremdriften ble lagret per bruker lå den under én felles nøkkel. Den
// tas over av første bruker som åpner siden, og fjernes så neste bruker i
// samme nettleser ikke arver den.
const readStored = (key: string): Set<string> => {
  try {
    const own = parse(window.localStorage.getItem(key));
    if (own) return own;
    const legacy = parse(window.localStorage.getItem(LEGACY_KEY));
    if (legacy) {
      window.localStorage.setItem(key, JSON.stringify([...legacy]));
      window.localStorage.removeItem(LEGACY_KEY);
      return legacy;
    }
  } catch {
    // Utilgjengelig localStorage: start tomt, basen fyller inn.
  }
  return new Set();
};

const writeStored = (key: string, ids: Set<string>) => {
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Full/utilgjengelig localStorage: fremdriften lever videre i minnet.
  }
};

export const useTutorialProgress = () => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const key = identity?.id != null ? keyFor(identity.id) : null;
  const [completed, setCompletedState] = useState<Set<string>>(() => new Set());
  const [loaded, setLoaded] = useState(false);
  // Speiler `completed` så endringer bygger på siste verdi uten å legge
  // sideeffekter i en state-updater.
  const current = useRef(completed);
  const setCompleted = useCallback((next: Set<string>) => {
    current.current = next;
    setCompletedState(next);
  }, []);

  const persist = useCallback(
    (next: Set<string>) => {
      if (key) writeStored(key, next);
      dataProvider.saveTutorialProgress?.([...next]).catch(() => {
        // Lagres lokalt; neste endring prøver basen igjen.
      });
    },
    [dataProvider, key],
  );

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const local = readStored(key);
    setCompleted(local);
    (async () => {
      try {
        const remote = await dataProvider.getTutorialProgress?.();
        if (cancelled) return;
        if (remote) {
          const next = new Set(remote);
          setCompleted(next);
          writeStored(key, next);
        } else if (remote === null && local.size > 0) {
          persist(local);
        }
      } catch {
        // Basen svarer ikke: bruk det lokale.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataProvider, key, persist, setCompleted]);

  const update = useCallback(
    (change: (prev: Set<string>) => Set<string>) => {
      const next = change(current.current);
      if (next === current.current) return;
      setCompleted(next);
      persist(next);
    },
    [persist, setCompleted],
  );

  const toggleExercise = useCallback(
    (id: string) =>
      update((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      }),
    [update],
  );

  const markDone = useCallback(
    (id: string) =>
      update((prev) => (prev.has(id) ? prev : new Set([...prev, id]))),
    [update],
  );

  const resetProgress = useCallback(() => update(() => new Set()), [update]);

  return { completed, loaded, toggleExercise, markDone, resetProgress };
};
